/**
 * PowerPoint parent tools — schemas and Office.js handler implementations.
 *
 * Each tool:
 *  1. Has a `schema` (ParentToolSchema) that is registered with the Usable embed.
 *  2. Has a `handler` that executes the Office.js operation and returns a plain object.
 *
 * All handlers wrap operations in PowerPoint.run() and call context.sync().
 * Read operations use .load() before sync and read values after.
 */

import { ParentToolSchema } from "@usable/office-embed";
import {
  executeToolCall,
  LONG_TOOL_TIMEOUT_MS,
  __resetToolExecutionForTests,
} from "@usable/office-embed";
import type { PptxTool, PptxToolHandler, IsSetSupported } from "./tool-types";
import { slideTools } from "./tools/slides";
import { textTools } from "./tools/text";
import { shapeTools } from "./tools/shapes";
import { tableTools } from "./tools/tables";
import { layoutTools } from "./tools/layout";
import { masterTools } from "./tools/master";
import { templateTools } from "./tools/templates";
import { screenshotTools } from "./tools/screenshot";

// Re-export the shared tool types for back-compat: existing tests import these
// from "./pptx-tools".
export type { PptxTool, PptxToolHandler, IsSetSupported } from "./tool-types";

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

const tools: PptxTool[] = [
  ...slideTools,
  ...textTools,
  ...shapeTools,
  ...tableTools,
  ...layoutTools,
  ...masterTools,
  ...templateTools,
  ...screenshotTools,
];

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
// (Dedup, serialization, timeouts, and Office.js error mapping live in
// ./execution.ts — see the WHY comments there.)

/**
 * Pure capability gate: keep every untagged tool, plus any tool whose
 * `minRequirementSet` the host supports per the given predicate. No live
 * Office dependency — pass a mocked `isSetSupported` to unit-test it.
 */
export function filterSupportedTools(
  candidateTools: PptxTool[],
  isSetSupported: IsSetSupported
): PptxTool[] {
  return candidateTools.filter(
    (t) =>
      t.minRequirementSet === undefined ||
      isSetSupported("PowerPointApi", t.minRequirementSet)
  );
}

/**
 * Live requirement-set probe. Guarded so it degrades to "supported" if the
 * requirements API is somehow unavailable — never gate a tool out by accident.
 */
function defaultIsSetSupported(name: string, minVersion?: string): boolean {
  try {
    return Office.context.requirements.isSetSupported(name, minVersion);
  } catch {
    return true;
  }
}

/**
 * Build the schemas to advertise to the AI, gated to what the running host
 * supports. Defaults to the live Office predicate; callers in a non-Office
 * context (tests) pass their own. MUST be called after `Office.onReady` — the
 * requirements API is only reliable once Office has initialised (see index.tsx).
 */
export function getSupportedPptxToolSchemas(
  isSetSupported: IsSetSupported = defaultIsSetSupported
): ParentToolSchema[] {
  return filterSupportedTools(tools, isSetSupported).map((t) => t.schema);
}

/**
 * Eager, ungated schema list (all tools). Retained for back-compat with callers
 * that bootstrap before Office is ready; prefer `getSupportedPptxToolSchemas()`.
 */
export const pptxToolSchemas: ParentToolSchema[] = tools.map((t) => t.schema);

const handlerMap: Map<string, PptxToolHandler> = new Map(
  tools.map((t) => [t.schema.name, t.handler])
);

/**
 * Map of tool name → the requirement set it needs. Used by `handlePptxToolCall`
 * to return a structured gated-error (instead of throwing) when a tool is
 * invoked on a host that can't run it.
 */
const requiredSetByTool: Map<string, string> = new Map(
  tools
    .filter((t) => t.minRequirementSet !== undefined)
    .map((t) => [t.schema.name, t.minRequirementSet as string])
);

/** Per-tool timeout overrides for legitimately slow Office.js work. */
const TOOL_TIMEOUT_OVERRIDES = new Map<string, number>([
  ["get_slide_screenshot", LONG_TOOL_TIMEOUT_MS],
  ["duplicate_slide", LONG_TOOL_TIMEOUT_MS],
  ["compose_slide", LONG_TOOL_TIMEOUT_MS],
  ["compose_deck", 120_000], // a whole deck in one call — many slides × shapes
]);

export async function handlePptxToolCall(
  toolName: string,
  args: unknown,
  requestId: string
): Promise<unknown> {
  const handler = handlerMap.get(toolName);
  if (!handler) {
    throw new Error(`Unknown PowerPoint tool: "${toolName}"`);
  }

  // Capability gate: a gated tool can still be invoked (e.g. a stale schema, or
  // a model that hallucinates it). Fail soft with a structured error the AI can
  // read, rather than crashing on an opaque Office API exception.
  const requiredSet = requiredSetByTool.get(toolName);
  if (requiredSet !== undefined && !defaultIsSetSupported("PowerPointApi", requiredSet)) {
    return {
      error: `PowerPoint tool "${toolName}" requires PowerPointApi ${requiredSet}, which this host does not support.`,
      requiredSet,
    };
  }

  return executeToolCall(toolName, args, () => handler(args as Record<string, unknown>), {
    timeoutMs: TOOL_TIMEOUT_OVERRIDES.get(toolName),
    requestId,
  });
}

/** Test-only: clear the execution-layer dedup state between cases. */
export function __resetPptxDedupForTests(): void {
  __resetToolExecutionForTests();
}
